import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CreditCard, Banknote, CheckCircle2, Store, MapPin, Truck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { useCart } from '../contexts/CartContext';
import { useSettings } from '../contexts/SettingsContext';
import { toast } from 'sonner';
import axios from 'axios';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const formatPrice = (p) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(p);

const getColombiaTodayString = () => {
  const now = new Date();
  const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
  const colombiaTime = new Date(utcTime + (3600000 * -5));
  const colombiaYear = colombiaTime.getFullYear();
  const colombiaMonth = String(colombiaTime.getMonth() + 1).padStart(2, '0');
  const colombiaDay = String(colombiaTime.getDate()).padStart(2, '0');
  return `${colombiaYear}-${colombiaMonth}-${colombiaDay}`;
};

export default function CheckoutPage() {
  const { whatsappNumber } = useSettings();
  const { items, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', barrio: '', date: '', observations: '' });
  const [paymentMethod, setPaymentMethod] = useState('transfer');
  const [submitting, setSubmitting] = useState(false);
  const [orderResult, setOrderResult] = useState(null);
  const [publicConfig, setPublicConfig] = useState(null);
  const [stores, setStores] = useState([]);
  const [deliveryType, setDeliveryType] = useState('pickup');
  const [selectedStore, setSelectedStore] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [deliveryZones, setDeliveryZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState(null);
  const [deliveryTimeSlot, setDeliveryTimeSlot] = useState('');
  const [paymentOption, setPaymentOption] = useState('full'); // 'full' (100%) | 'deposit' (50%)

  // Check if any product in cart is pickup only
  const hasPickupOnlyItem = items.some(item => {
    const methods = item.product?.delivery_methods || item.delivery_methods;
    return methods && !methods.includes('delivery');
  });

  const hasChocoarequipeClasica = items.some(item => {
    const name = (item.product?.name || item.name || '').toLowerCase();
    const cat = (item.product?.category || item.category || '').toLowerCase();
    return name.includes('chocoarequipe') && cat === 'clasicas';
  });

  const isPickupMandatory = hasPickupOnlyItem || hasChocoarequipeClasica;

  const hasTortaInCart = items.some(item => {
    const cat = item.product?.category || item.category || '';
    return ['refrigeradas', 'clasicas', 'saludables', 'tortas', 'tortas-especiales', 'tortas-personalizadas', 'tortas-tematicas'].includes(cat);
  });

  const isToday = form.date === getColombiaTodayString();

  useEffect(() => {
    if (isToday && deliveryType === 'delivery') {
      if (deliveryTimeSlot && deliveryTimeSlot !== '3:00 PM - 6:00 PM') {
        setDeliveryTimeSlot('');
      }
    }
  }, [form.date, deliveryType]);

  useEffect(() => {
    if (deliveryType === 'pickup' && hasTortaInCart) {
      if (deliveryTimeSlot && !['2:00 PM', '3:00 PM - 6:00 PM'].includes(deliveryTimeSlot)) {
        setDeliveryTimeSlot('');
      }
    }
  }, [deliveryType, hasTortaInCart, deliveryTimeSlot]);

  useEffect(() => {
    if (isPickupMandatory) {
      setDeliveryType('pickup');
    }
  }, [isPickupMandatory]);

  useEffect(() => {
    if (hasChocoarequipeClasica && stores.length > 0) {
      const bostonStore = stores.find(s => (s.name || '').toLowerCase().includes('boston'));
      if (bostonStore && selectedStore?.id !== bostonStore.id) {
        setSelectedStore(bostonStore);
      }
    }
  }, [hasChocoarequipeClasica, stores, selectedStore]);

  useEffect(() => {
    const fetchPublicConfig = async () => {
      try {
        const { data } = await axios.get(`${API}/settings/public`);
        setPublicConfig(data);
        if (data.active_gateway && data.active_gateway !== 'none') {
          setPaymentMethod('gateway');

          // Inject script
          const script = document.createElement('script');
          if (data.active_gateway === 'wompi') {
            script.src = 'https://checkout.wompi.co/widget.js';
          } else if (data.active_gateway === 'epayco') {
            script.src = 'https://checkout.epayco.co/checkout.js';
          }
          script.async = true;
          document.body.appendChild(script);
        }
      } catch (err) {
        console.error('Error fetching public config:', err);
      }
    };
    const fetchStores = async () => {
      try {
        const { data } = await axios.get(`${API}/stores`);
        setStores(data);
        if (data.length > 0) {
          setSelectedStore(data[0]);
        }
      } catch (err) {
        console.error('Error fetching stores:', err);
      }
    };
    const fetchZones = async () => {
      try {
        const { data } = await axios.get(`${API}/delivery-zones`);
        setDeliveryZones(data);
      } catch (err) {
        console.error('Error fetching delivery zones:', err);
      }
    };
    fetchPublicConfig();
    fetchStores();
    fetchZones();
  }, []);

  const handleChange = (e) => setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));

  const normalizePhone = (val) => {
    if (!val) return '';
    const digits = String(val).replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('57')) return digits;
    return `57${digits}`;
  };

  const handlePhoneChange = (e) => {
    const cleaned = e.target.value.replace(/\D/g, '');
    setForm(prev => ({ ...prev, phone: cleaned }));
  };

  const handlePhoneBlur = () => {
    if (form.phone) {
      setForm(prev => ({ ...prev, phone: normalizePhone(prev.phone) }));
    }
  };

  const getRequiredPrepDays = () => {
    let maxDays = 0;
    items.forEach(item => {
      const cat = item.product?.category || item.category || '';
      const isSpecial = ['tortas-especiales', 'tortas-personalizadas', 'tortas-tematicas', 'grados', 'comunion'].includes(cat) ||
                        (item.name || '').toLowerCase().includes('especial');

      if (item.product?.pide_hoy && !isSpecial) {
        return;
      }
      let prepDays = item.size?.prep_days ?? item.product?.preparation_days ?? 2;
      if (isSpecial) {
        prepDays = Math.max(prepDays, 3);
      }
      if (prepDays > maxDays) {
        maxDays = prepDays;
      }
    });
    return maxDays;
  };

  const getColombiaMinDeliveryDateString = () => {
    const prepDays = getRequiredPrepDays();
    const now = new Date();
    const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
    const colombiaTime = new Date(utcTime + (3600000 * -5));
    let additionalDays = prepDays;
    if (prepDays === 0) {
      if (colombiaTime.getHours() >= 15) {
        additionalDays = 1;
      }
    }
    colombiaTime.setDate(colombiaTime.getDate() + additionalDays);
    const colombiaYear = colombiaTime.getFullYear();
    const colombiaMonth = String(colombiaTime.getMonth() + 1).padStart(2, '0');
    const colombiaDay = String(colombiaTime.getDate()).padStart(2, '0');
    return `${colombiaYear}-${colombiaMonth}-${colombiaDay}`;
  };



  const hasCreativeCake = items.some(item => {
    const cat = item.product?.category || item.category;
    return ['tortas-personalizadas', 'tortas-tematicas', 'tortas-especiales'].includes(cat);
  });

  const deliveryFee = (deliveryType === 'delivery' && selectedZone)
    ? (hasCreativeCake ? (selectedZone.delivery_creative ?? 13900) : (selectedZone.delivery_normal ?? 8900))
    : 0;

  const finalTotal = total + deliveryFee;
  const isCOD = paymentMethod === 'cash';
  const effectiveDepositRequired = isCOD ? 0 : (paymentOption === 'full' ? finalTotal : finalTotal * 0.5);
  const effectiveInitialPayment = isCOD ? 0 : (paymentOption === 'full' ? finalTotal : finalTotal * 0.5);
  const finalDeposit = effectiveDepositRequired;

  const generateWhatsAppMessage = () => {
    const itemsText = items.map(item => `- ${item.name} (${item.size?.name || 'Estándar'}) x${item.quantity || 1}`).join('\n');
    const deliveryText = deliveryType === 'delivery'
      ? `\n🚚 Domicilio a: ${form.address || ''} (Barrio: ${form.barrio || ''})\n📍 Zona: ${selectedZone?.name || ''} (Ruta: ${selectedZone?.route || ''})\n⏰ Horario: ${deliveryTimeSlot || ''}\n💵 Costo Domicilio: ${formatPrice(deliveryFee)}`
      : `\n🏪 Retiro en Sede: ${selectedStore?.name || ''}`;
    
    return encodeURIComponent(`Hola Keiks! Quiero realizar el siguiente pedido:\n\n${itemsText}\n${deliveryText}\n\n💰 Total Productos: ${formatPrice(total)}\n💳 Total con Envío: ${formatPrice(finalTotal)}\n💵 Depósito (50%): ${formatPrice(finalDeposit)}\n\nDatos de contacto:\n- Nombre: ${form.name || ''}\n- Teléfono: ${form.phone || ''}\n- Correo: ${form.email || ''}\n- Observaciones: ${form.observations || 'Ninguna'}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.name) { toast.error('Completa tu nombre'); return; }
    if (deliveryType === 'delivery' && !selectedZone) { toast.error('Selecciona la zona de entrega'); return; }
    if (deliveryType === 'delivery' && !form.address) { toast.error('Completa la dirección de entrega'); return; }
    if (deliveryType === 'delivery' && !form.barrio) { toast.error('Completa el barrio de entrega'); return; }
    if (deliveryType === 'delivery' && !deliveryTimeSlot) { toast.error('Selecciona el horario de entrega'); return; }
    if (deliveryType === 'pickup' && !deliveryTimeSlot) { toast.error('Selecciona el horario de retiro en tienda'); return; }
    if (!selectedStore) { toast.error('Selecciona una sede'); return; }
    if (!form.date) { toast.error('Selecciona la fecha de entrega'); return; }
    if (!acceptTerms) { toast.error('Debes aceptar los términos y condiciones de entrega para continuar.'); return; }

    // Date validation for preparation days
    const minDateStr = getColombiaMinDeliveryDateString();
    if (form.date < minDateStr) {
      const prepDays = getRequiredPrepDays();
      if (prepDays === 0) {
        toast.error('La fecha de entrega no puede ser anterior a hoy.');
      } else {
        toast.error(`Este pedido requiere mínimo ${prepDays} día(s) de preparación. La fecha mínima permitida es ${minDateStr}.`);
      }
      return;
    }

    const todayStr = getColombiaTodayString();
    if (form.date === todayStr) {
      const now = new Date();
      const utcTime = now.getTime() + now.getTimezoneOffset() * 60000;
      const colombiaTime = new Date(utcTime + (3600000 * -5));
      if (colombiaTime.getHours() >= 15) {
        toast.error('Para pedidos del mismo día, el horario límite es hasta las 3:00 PM. Por favor selecciona una fecha posterior.');
        return;
      }
    }

    const cleanPhone = normalizePhone(form.phone);
    if (!cleanPhone || cleanPhone.length < 10) {
      toast.error('Por favor ingresa un número de teléfono válido (sin espacios)');
      return;
    }

    setSubmitting(true);
    try {
      const orderPayload = {
        customer_name: form.name,
        customer_email: form.email,
        customer_phone: cleanPhone,
        items: items.map(item => ({
          productId: item.productId,
          name: item.name,
          image: item.product?.image || item.image || "",
          size: item.size?.name || "",
          filling: item.filling?.name || "",
          flavor: item.flavor?.name || "",
          complements: item.complements?.map(c => c.name) || [],
          complements_with_prices: item.complements?.map(c => ({ name: c.name, price: c.price })) || [],
          observations: item.observations || "",
          subtotal: item.subtotal,
          quantity: item.quantity || 1,
          base_price: item.size?.price ?? item.product?.base_price ?? 0,
          filling_price: item.filling?.price ?? 0,
          flavor_price: item.flavor?.price ?? 0,
        })),
        delivery_type: deliveryType,
        store_id: selectedStore.id,
        store_name: selectedStore.name,
        delivery_address: deliveryType === 'delivery' ? `${form.address} - Barrio: ${form.barrio} (Zona: ${selectedZone?.name}, Horario: ${deliveryTimeSlot})` : "",
        delivery_date: form.date || "",
        observations: form.observations || "",
        payment_method: paymentMethod,
        payment_option: paymentOption,
        delivery_fee: deliveryFee,
        delivery_time_slot: deliveryTimeSlot
      };

      const { data: createdOrder } = await axios.post(`${API}/orders`, orderPayload);

      const activeGateway = publicConfig?.active_gateway || 'none';
      if (paymentMethod === 'gateway' && activeGateway !== 'none') {
        const amountToCharge = paymentOption === 'full' ? (createdOrder.total || finalTotal) : (createdOrder.deposit_required || (finalTotal * 0.5));
        const WompiClass = window.WidgetCheckout || window.WompiCheckout;
        if (activeGateway === 'wompi' && WompiClass) {
          const checkout = new WompiClass({
            publicKey: publicConfig?.wompi_public_key || 'pub_prod_dr6VbUQaM0VnSPjoCAnJc2PljE4YB76J',
            reference: createdOrder.tracking_code,
            amountInCents: Math.round(amountToCharge * 100),
            currency: 'COP',
            redirectUrl: `${window.location.origin}/tracking?code=${createdOrder.tracking_code}`
          });
          checkout.open((result) => {
            const tx = result.transaction;
            if (tx && (tx.status === 'APPROVED' || tx.status === 'PENDING')) {
              setOrderResult(createdOrder);
              clearCart();
              toast.success('Pago procesado! Confirma por WhatsApp');
            } else {
              toast.error('El pago no fue aprobado.');
            }
          });
          setSubmitting(false);
          return;
        }

        if (activeGateway === 'epayco' && window.ePayco) {
          const handler = window.ePayco.checkout.configure({
            key: publicConfig.epayco_public_key,
            test: publicConfig.epayco_mode === 'sandbox'
          });
          handler.open({
            name: "Keiks Repostería",
            description: paymentOption === 'full' ? `Pago total pedido ${createdOrder.tracking_code}` : `Depósito pedido ${createdOrder.tracking_code}`,
            invoice: createdOrder.tracking_code,
            currency: "cop",
            amount: amountToCharge,
            tax_base: "0",
            tax: "0",
            country: "co",
            lang: "es",
            external: "false",
            extra1: createdOrder.id,
            response: `${window.location.origin}/tracking?code=${createdOrder.tracking_code}`,
          });
          setOrderResult(createdOrder);
          clearCart();
          toast.success('Abriendo pasarela de pagos...');
          setSubmitting(false);
          return;
        }
      }

      setOrderResult(createdOrder);
      clearCart();
      toast.success('Pedido creado! Confirma por WhatsApp');
    } catch (err) {
      console.error(err);
      toast.error('Error al crear el pedido');
    } finally {
      setSubmitting(false);
    }
  };

  if (orderResult) {
    const hasPromoGift = orderResult.total >= 50000;
    const promoText = hasPromoGift ? " (Incluye Regalo Gratis: Vela Volcán y Letrero Feliz Cumpleaños)" : "";
    const amountToPay = paymentOption === 'full' ? orderResult.total : orderResult.deposit_required;
    const whatsappMsg = encodeURIComponent(`Hola Keiks! Acabo de realizar el pedido ${orderResult.tracking_code}${promoText}. Mi nombre es ${orderResult.customer_name}. Total: ${formatPrice(orderResult.total)}. ${paymentOption === 'full' ? 'Pago Total (100%)' : 'Deposito (50%)'}: ${formatPrice(amountToPay)}`);
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center animate-fade-in" data-testid="order-success">
        <CheckCircle2 className="w-16 h-16 mx-auto text-[#a0d5ba] mb-4" />
        <h1 className="font-heading text-3xl font-bold text-[#46371c] mb-3">Pedido Confirmado!</h1>
        <p className="font-body text-[#75664d] mb-6">Tu codigo de seguimiento es:</p>
        <p className="font-heading text-4xl font-bold text-[#46371c] mb-2" data-testid="tracking-code">{orderResult.tracking_code}</p>
        <Card className="p-6 border border-[#e5dfd3] rounded-2xl bg-[#eef6f2] text-left my-8">
          <h3 className="font-heading text-lg font-semibold text-[#46371c] mb-3">Detalles del pago</h3>
          <div className="font-body text-sm space-y-1">
            <p className="text-[#75664d]">Total: <span className="text-[#46371c] font-semibold">{formatPrice(orderResult.total)}</span></p>
            <p className="text-[#75664d]">Monto a pagar ({paymentOption === 'full' ? '100%' : '50%'}): <span className="text-[#46371c] font-semibold">{formatPrice(amountToPay)}</span></p>
            <p className="text-[#75664d]">Metodo: <span className="text-[#46371c] font-medium">{paymentMethod === 'gateway' ? 'Pago en línea' : paymentMethod === 'transfer' ? 'Transferencia' : 'Contra entrega'}</span></p>
            {hasPromoGift && (
              <div className="mt-3 pt-3 border-t border-[#e5dfd3]/60 text-[#347a54]">
                <span className="font-bold">🎁 Regalo de Promoción Incluido:</span>
                <ul className="list-disc pl-5 mt-1 text-xs">
                  <li>Vela Volcán (Gratis)</li>
                  <li>Letrero de Feliz Cumpleaños (Gratis)</li>
                </ul>
              </div>
            )}
            {paymentMethod === 'transfer' && (
              <div className="mt-4 pt-4 border-t border-dashed border-[#e5dfd3] text-xs space-y-1 text-[#75664d] bg-[#fcf7ed]/50 p-3 rounded-xl border border-[#e8c894]/40">
                <p className="font-semibold text-[#b45309]">Datos para tu transferencia ({publicConfig?.bank_name || 'Bancolombia'} {publicConfig?.bank_account_type || 'Ahorros'}):</p>
                <p>Banco: <strong>{publicConfig?.bank_name || 'Bancolombia'}</strong></p>
                <p>Tipo de Cuenta: <strong>{publicConfig?.bank_account_type || 'Ahorros'}</strong></p>
                <p>Número de Cuenta: <strong className="font-mono text-[#b45309]">{publicConfig?.bank_account_number || 'Consultar por WhatsApp'}</strong></p>
                <p>Titular: <strong>{publicConfig?.bank_account_holder || 'Keiks Repostería S.A.S'}</strong></p>
                <p className="pt-1">Por favor envía el comprobante de pago {paymentOption === 'full' ? `del 100% (${formatPrice(orderResult.total)})` : `del 50% (${formatPrice(orderResult.deposit_required)})`} a nuestro WhatsApp para aprobar tu pedido.</p>
              </div>
            )}
          </div>
        </Card>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <a href={`https://wa.me/${whatsappNumber}?text=${whatsappMsg}`} target="_blank" rel="noopener noreferrer">
            <Button className="bg-[#25D366] text-white hover:bg-[#20bd5a] rounded-full px-8 font-body" data-testid="confirm-whatsapp-btn">
              Confirmar por WhatsApp
            </Button>
          </a>
          <Link to={`/tracking?code=${orderResult.tracking_code}`}>
            <Button variant="outline" className="border-[#46371c] text-[#46371c] rounded-full px-8 font-body" data-testid="track-order-btn">
              Seguir mi pedido
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    navigate('/carrito');
    return null;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 animate-fade-in" data-testid="checkout-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 font-body text-sm text-[#75664d] hover:text-[#46371c] mb-6" data-testid="back-to-cart">
        <ArrowLeft className="w-4 h-4" /> Volver al carrito
      </button>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#46371c] mb-8">Finalizar Pedido</h1>

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6 border border-[#e5dfd3] rounded-2xl">
              <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-4">Datos de contacto</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="font-body text-sm text-[#75664d] mb-1 block">Nombre completo *</label>
                  <input name="name" value={form.name} onChange={handleChange} required className="w-full h-11 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="checkout-name" />
                </div>
                <div>
                  <label className="font-body text-sm text-[#75664d] mb-1 block">Email (Opcional)</label>
                  <input name="email" type="email" value={form.email} onChange={handleChange} className="w-full h-11 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="checkout-email" />
                </div>
                <div>
                  <label className="font-body text-sm text-[#75664d] mb-1 block">Teléfono (WhatsApp) *</label>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={handlePhoneChange}
                    onBlur={handlePhoneBlur}
                    placeholder="Ej: 3001234567"
                    required
                    className="w-full h-11 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                    data-testid="checkout-phone"
                  />
                  <p className="font-body text-[10px] text-[#75664d] mt-1">Solo números sin espacios. Agregamos el 57 automáticamente.</p>
                </div>
                <div>
                  <label className="font-body text-sm text-[#75664d] mb-1 block">Fecha de entrega *</label>
                  <input name="date" type="date" value={form.date} onChange={handleChange} min={getColombiaMinDeliveryDateString()} className="w-full h-11 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]" data-testid="checkout-date" />
                </div>
              </div>

              <div className="mt-6 border-t border-[#e5dfd3]/60 pt-6">
                <label className="font-body text-sm font-semibold text-[#46371c] mb-2 block">Método de entrega *</label>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <button
                    type="button"
                    onClick={() => setDeliveryType('pickup')}
                    data-testid="delivery-type-pickup"
                    className={`p-4 rounded-xl border text-center transition-all flex flex-col items-center justify-center gap-2 ${deliveryType === 'pickup'
                      ? 'border-[#a0d5ba] bg-[#eef6f2] text-[#46371c] shadow-sm'
                      : 'border-[#e5dfd3] text-[#75664d] hover:border-[#a0d5ba]/50'
                      }`}
                  >
                    <Store className="w-5 h-5 text-[#46371c]" />
                    <span className="font-body text-sm font-medium">Recoger en tienda</span>
                  </button>
                  <button
                    type="button"
                    disabled={isPickupMandatory}
                    onClick={() => setDeliveryType('delivery')}
                    data-testid="delivery-type-delivery"
                    className={`p-4 rounded-xl border text-center transition-all flex flex-col items-center justify-center gap-2 ${isPickupMandatory ? 'opacity-50 cursor-not-allowed bg-gray-50 border-gray-200 text-gray-400' :
                      deliveryType === 'delivery'
                        ? 'border-[#a0d5ba] bg-[#eef6f2] text-[#46371c] shadow-sm'
                        : 'border-[#e5dfd3] text-[#75664d] hover:border-[#a0d5ba]/50'
                      }`}
                  >
                    <Truck className="w-5 h-5 text-[#46371c]" />
                    <span className="font-body text-sm font-medium">Servicio a domicilio</span>
                  </button>
                </div>

                {hasChocoarequipeClasica ? (
                  <p className="text-xs text-amber-900 bg-amber-50 border border-amber-300 rounded-xl p-3 font-body mt-2 mb-4 leading-relaxed">
                    ⚠️ <strong>Aviso:</strong> Tu carrito contiene la <strong>Torta Chocoarequipe (Clásicas)</strong>. Este producto solo puede ser reclamado en nuestra <strong>Sede Boston</strong>.
                  </p>
                ) : hasPickupOnlyItem ? (
                  <p className="text-xs text-red-500 font-body mt-2 mb-4">
                    * Tu carrito contiene productos exclusivos de tienda física. El envío a domicilio no está disponible.
                  </p>
                ) : null}

                {deliveryType === 'pickup' ? (
                  <div className="space-y-3 animate-fade-in">
                    <label className="font-body text-sm font-semibold text-[#75664d] mb-1 block">Selecciona la sede de retiro *</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {(hasChocoarequipeClasica ? stores.filter(s => (s.name || '').toLowerCase().includes('boston')) : stores).map((store) => (
                        <button
                          key={store.id}
                          type="button"
                          onClick={() => setSelectedStore(store)}
                          data-testid={`select-store-${store.id}`}
                          className={`p-4 rounded-xl border text-left transition-all flex flex-col justify-between ${selectedStore?.id === store.id
                            ? 'border-[#a0d5ba] bg-[#eef6f2] ring-1 ring-[#a0d5ba]'
                            : 'border-[#e5dfd3] hover:border-[#a0d5ba]/50 bg-white'
                            }`}
                        >
                          <div>
                            <div className="flex items-center justify-between gap-1">
                              <p className="font-body text-sm font-semibold text-[#46371c]">{store.name}</p>
                              {hasChocoarequipeClasica && (
                                <Badge className="bg-emerald-100 text-emerald-800 text-[10px] border-emerald-300 shrink-0">
                                  Sede Exclusiva
                                </Badge>
                              )}
                            </div>
                            <p className="font-body text-xs text-[#75664d] mt-1 flex items-center gap-1"><MapPin className="w-3 h-3 flex-shrink-0" />{store.address}</p>
                          </div>
                          <p className="font-body text-[10px] text-[#75664d] mt-2 border-t border-[#e5dfd3]/40 pt-1">{store.hours}</p>
                        </button>
                      ))}
                    </div>
                    
                    <div className="mt-4">
                      <label className="font-body text-sm font-semibold text-[#75664d] mb-1.5 block">Horario de Retiro en Tienda *</label>
                      <Select value={deliveryTimeSlot || undefined} onValueChange={setDeliveryTimeSlot}>
                        <SelectTrigger className="rounded-xl border-[#e5dfd3] h-11 bg-white">
                          <SelectValue placeholder="Selecciona un horario de retiro" />
                        </SelectTrigger>
                        <SelectContent>
                          {!hasTortaInCart && <SelectItem value="9:00 AM - 12:00 M">9:00 AM - 12:00 M</SelectItem>}
                          {!hasTortaInCart && <SelectItem value="12:00 M - 3:00 PM">12:00 M - 3:00 PM</SelectItem>}
                          <SelectItem value="2:00 PM">2:00 PM</SelectItem>
                          <SelectItem value="3:00 PM - 6:00 PM">3:00 PM - 6:00 PM</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                                ) : (
                  <div className="space-y-4 animate-fade-in">
                    <div>
                      <label className="font-body text-sm text-[#75664d] mb-1.5 block font-semibold">Zona de Entrega *</label>
                      <Select
                        value={selectedZone?.id || undefined}
                        onValueChange={(zoneId) => {
                          const zone = deliveryZones.find(z => z.id === zoneId);
                          setSelectedZone(zone);
                          if (zone) {
                            const matchingStore = stores.find(s => s.id === zone.store_id || s.name === zone.store_name);
                            if (matchingStore) setSelectedStore(matchingStore);
                            if (isToday) {
                              if (zone.schedule_3_6) setDeliveryTimeSlot('3:00 PM - 6:00 PM');
                              else setDeliveryTimeSlot('');
                            } else {
                              if (zone.schedule_9_12) setDeliveryTimeSlot('9:00 AM - 12:00 M');
                              else if (zone.schedule_12_3) setDeliveryTimeSlot('12:00 M - 3:00 PM');
                              else if (zone.schedule_3_6) setDeliveryTimeSlot('3:00 PM - 6:00 PM');
                              else setDeliveryTimeSlot('');
                            }
                          }
                        }}
                      >
                        <SelectTrigger className="rounded-xl border-[#e5dfd3] h-11" data-testid="checkout-zone-select">
                          <SelectValue placeholder="Selecciona tu zona de cobertura" />
                        </SelectTrigger>
                        <SelectContent>
                          {deliveryZones.map(zone => (
                            <SelectItem key={zone.id} value={zone.id}>
                              {zone.name} ({zone.route || 'Sin Ruta'})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {selectedZone && (
                        <p className="text-xs text-[#75664d] mt-1 font-body">
                          Tarifa de envío: <span className="font-semibold text-[#46371c]">{formatPrice(hasCreativeCake ? selectedZone.delivery_creative : selectedZone.delivery_normal)}</span> (Normal: {formatPrice(selectedZone.delivery_normal)} / Creativas: {formatPrice(selectedZone.delivery_creative)})
                        </p>
                      )}
                    </div>

                    {selectedZone && (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="font-body text-sm text-[#75664d] mb-1 block">Dirección de entrega *</label>
                            <input
                              name="address"
                              value={form.address}
                              onChange={handleChange}
                              required={deliveryType === 'delivery'}
                              placeholder="Calle, Carrera, Edificio, Apto..."
                              className="w-full h-11 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                              data-testid="checkout-address"
                            />
                          </div>
                          <div>
                            <label className="font-body text-sm text-[#75664d] mb-1 block">Barrio *</label>
                            <input
                              name="barrio"
                              value={form.barrio}
                              onChange={handleChange}
                              required={deliveryType === 'delivery'}
                              placeholder="Ej: Bellavista, La Cumbre..."
                              className="w-full h-11 px-4 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba]"
                              data-testid="checkout-barrio"
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <div>
                            <label className="font-body text-sm text-[#75664d] mb-1.5 block font-semibold">Horario de Domicilio *</label>
                            <Select value={deliveryTimeSlot || undefined} onValueChange={setDeliveryTimeSlot}>
                              <SelectTrigger className="rounded-xl border-[#e5dfd3] h-11">
                                <SelectValue placeholder="Selecciona un horario" />
                              </SelectTrigger>
                              <SelectContent>
                                {!isToday && selectedZone.schedule_9_12 && <SelectItem value="9:00 AM - 12:00 M">9:00 AM - 12:00 M</SelectItem>}
                                {!isToday && selectedZone.schedule_12_3 && <SelectItem value="12:00 M - 3:00 PM">12:00 M - 3:00 PM</SelectItem>}
                                {selectedZone.schedule_3_6 && <SelectItem value="3:00 PM - 6:00 PM">3:00 PM - 6:00 PM</SelectItem>}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="font-body text-sm text-[#75664d] mb-1.5 block">Sede de Despacho Asignada</label>
                            <div className="h-11 px-4 rounded-xl border border-[#e5dfd3] bg-gray-50 flex items-center font-body text-sm text-[#46371c]">
                              <Store className="w-4 h-4 mr-2 text-[#a0d5ba]" />
                              {selectedStore ? selectedStore.name : 'Asignando sede...'}
                            </div>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              <div className="mt-4">
                <label className="font-body text-sm text-[#75664d] mb-1 block">Observaciones</label>
                <textarea name="observations" value={form.observations} onChange={handleChange} rows={3} className="w-full p-3 rounded-xl border border-[#e5dfd3] font-body text-sm text-[#46371c] focus:outline-none focus:border-[#a0d5ba] resize-none" data-testid="checkout-observations" />
              </div>
            </Card>

            <Card className="p-6 border border-[#e5dfd3] rounded-2xl">
              <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-4">Metodo de pago</h3>
              <div className={`grid grid-cols-1 ${publicConfig?.active_gateway && publicConfig.active_gateway !== 'none' && publicConfig?.accept_online_payments ? 'sm:grid-cols-3' : 'sm:grid-cols-2'} gap-3`}>
                {publicConfig?.active_gateway && publicConfig.active_gateway !== 'none' && publicConfig?.accept_online_payments && (
                  <button type="button" onClick={() => { setPaymentMethod('gateway'); setPaymentOption('full'); }} data-testid="payment-gateway"
                    className={`p-4 rounded-xl border text-left transition-all ${paymentMethod === 'gateway' ? 'border-[#a0d5ba] bg-[#eef6f2]' : 'border-[#e5dfd3]'}`}>
                    <CreditCard className="w-5 h-5 text-[#46371c] mb-2" />
                    <p className="font-body text-sm font-medium text-[#46371c]">Pago en línea</p>
                    <p className="font-body text-xs text-[#75664d]">Pagar con {publicConfig.active_gateway === 'wompi' ? 'Wompi' : 'ePayco'}</p>
                  </button>
                )}
                <button type="button" onClick={() => setPaymentMethod('transfer')} data-testid="payment-transfer"
                  className={`p-4 rounded-xl border text-left transition-all ${paymentMethod === 'transfer' ? 'border-[#a0d5ba] bg-[#eef6f2]' : 'border-[#e5dfd3]'}`}>
                  <CreditCard className="w-5 h-5 text-[#46371c] mb-2" />
                  <p className="font-body text-sm font-medium text-[#46371c]">Transferencia bancaria</p>
                  <p className="font-body text-xs text-[#75664d]">Deposito 50% por transferencia</p>
                </button>
                <button type="button" onClick={() => setPaymentMethod('cash')} data-testid="payment-cash"
                  className={`p-4 rounded-xl border text-left transition-all ${paymentMethod === 'cash' ? 'border-[#a0d5ba] bg-[#eef6f2]' : 'border-[#e5dfd3]'}`}>
                  <Banknote className="w-5 h-5 text-[#46371c] mb-2" />
                  <p className="font-body text-sm font-medium text-[#46371c]">Contra entrega</p>
                  <p className="font-body text-xs text-[#75664d]">Sin abono previo (Pagas al recibir)</p>
                </button>
              </div>

              {paymentMethod === 'gateway' && (
                <div className="mt-4 p-3 bg-white border border-[#a0d5ba] rounded-xl space-y-1 font-body text-xs animate-fade-in">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-[#46371c]">Pago en Línea (100% Requerido):</span>
                    <span className="text-sm font-bold text-[#46371c]">{formatPrice(finalTotal)}</span>
                  </div>
                  <p className="text-[11px] text-[#75664d] leading-relaxed">* Si seleccionas pago en línea, debes realizar el pago del 100% del valor total.</p>
                </div>
              )}

              {paymentMethod === 'transfer' && (
                <div className="mt-4 p-3 bg-white border border-[#e5dfd3] rounded-xl space-y-2 font-body text-xs animate-fade-in">
                  <label className="font-bold text-[#46371c] block">Selecciona el monto a transferir:</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setPaymentOption('full')}
                      className={`p-2.5 rounded-lg border text-left flex flex-col justify-between transition-all ${
                        paymentOption === 'full' ? 'border-[#a0d5ba] bg-[#eef6f2] font-semibold text-[#46371c] ring-1 ring-[#a0d5ba]' : 'border-[#e5dfd3] text-[#75664d]'
                      }`}
                    >
                      <span>Pagar el 100%</span>
                      <span className="text-sm font-bold text-[#46371c] mt-1">{formatPrice(finalTotal)}</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setPaymentOption('deposit')}
                      className={`p-2.5 rounded-lg border text-left flex flex-col justify-between transition-all ${
                        paymentOption === 'deposit' ? 'border-[#a0d5ba] bg-[#eef6f2] font-semibold text-[#46371c] ring-1 ring-[#a0d5ba]' : 'border-[#e5dfd3] text-[#75664d]'
                      }`}
                    >
                      <span>Pagar solo Abono (50%)</span>
                      <span className="text-sm font-bold text-[#46371c] mt-1">{formatPrice(finalTotal * 0.5)}</span>
                    </button>
                  </div>
                </div>
              )}

              {paymentMethod === 'cash' && (
                <div className="mt-4 bg-[#eef6f2] border border-[#a0d5ba] text-[#347a54] rounded-2xl p-4 space-y-1 font-body text-xs animate-fade-in">
                  <p className="font-bold text-sm text-[#46371c]">🚚 Pago Contra Entrega (Sin Abono Previo)</p>
                  <p>Pagas el 100% de tu pedido al momento de recibirlo ({formatPrice(finalTotal)}). No requiere ningún depósito previo.</p>
                </div>
              )}

              {paymentMethod === 'transfer' && (
                <div className="mt-4 bg-[#fffbeb] border border-[#fef3c7] text-[#92400e] rounded-2xl p-4 space-y-2 font-body text-xs leading-relaxed animate-fade-in">
                  <p className="font-bold text-[#b45309] text-sm">Información de Transferencia Bancaria</p>
                  <p>Para confirmar tu pedido, por favor realiza la transferencia de <strong>{paymentOption === 'full' ? `el 100% (${formatPrice(finalTotal)})` : `el 50% (${formatPrice(finalTotal * 0.5)})`}</strong> a nuestra cuenta:</p>
                  <div className="bg-[#fef9c3] p-3 rounded-xl border border-[#fef08a] space-y-1 font-mono text-[#78350f]">
                    <p><strong>Banco:</strong> {publicConfig?.bank_name || 'Bancolombia'}</p>
                    <p><strong>Tipo de Cuenta:</strong> {publicConfig?.bank_account_type || 'Ahorros'}</p>
                    <p><strong>Número:</strong> <span className="font-bold text-[#b45309]">{publicConfig?.bank_account_number || 'Consultar por WhatsApp'}</span></p>
                    <p><strong>Titular:</strong> {publicConfig?.bank_account_holder || 'Keiks Repostería S.A.S'}</p>
                  </div>
                  <p className="mt-2 text-[#78350f]">
                    Una vez realizada la transferencia, envía el comprobante de pago a nuestro WhatsApp
                    <a href={`https://wa.me/${whatsappNumber}`} target="_blank" rel="noopener noreferrer" className="ml-1 font-bold text-[#25d366] hover:underline inline-flex items-center gap-1">
                      +{whatsappNumber}
                    </a>
                    indicando el <strong>número del pedido</strong> que recibirás a continuación.
                  </p>
                </div>
              )}
            </Card>
          </div>

          <div>
            <Card className="p-6 border border-[#e5dfd3] rounded-2xl bg-[#eef6f2] sticky top-24" data-testid="checkout-summary">
              <h3 className="font-heading text-xl font-semibold text-[#46371c] mb-4">Resumen</h3>
              <div className="space-y-3 mb-4">
                {items.map(item => (
                  <div key={item.cartId} className="flex gap-3">
                    <img src={item.image} alt="" className="w-12 h-12 rounded-lg object-cover" />
                    <div className="flex-1 min-w-0">
                      <p className="font-body text-sm font-medium text-[#46371c] truncate">{item.name}</p>
                      <p className="font-body text-xs text-[#75664d]">
                        {item.size?.name}
                        {item.flavor?.name ? ` | ${item.flavor.name}` : ''}
                      </p>
                      {item.complements?.length > 0 && (
                        <p className="font-body text-[11px] text-[#75664d]">
                          Adiciones: {item.complements.map(c => `${c.name}${c.quantity > 1 ? ` (x${c.quantity})` : ''}`).join(', ')}
                        </p>
                      )}
                    </div>
                    <span className="font-body text-sm font-medium text-[#46371c]">{formatPrice(item.subtotal)}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-[#e5dfd3] pt-3 space-y-2 font-body text-sm">
                <div className="flex justify-between"><span className="text-[#75664d]">Subtotal</span><span className="text-[#46371c]">{formatPrice(total)}</span></div>
                {deliveryType === 'delivery' && (
                  <div className="flex justify-between"><span className="text-[#75664d]">Domicilio ({selectedZone ? selectedZone.name : 'No seleccionada'})</span><span className="text-[#46371c]">{selectedZone ? formatPrice(deliveryFee) : 'A calcular'}</span></div>
                )}
                <div className="flex justify-between font-semibold text-base"><span className="text-[#46371c]">Total</span><span className="text-[#46371c]">{formatPrice(finalTotal)}</span></div>
                <div className="flex justify-between text-[#347a54] font-semibold text-xs">
                  <span>{isCOD ? 'Abono previo requerido:' : (paymentOption === 'full' ? 'Pago a realizar ahora (100%):' : 'Pago a realizar ahora (Abono 50%):')}</span>
                  <span>{formatPrice(finalDeposit)}</span>
                </div>
              </div>
              <div className="mt-4 pt-3 border-t border-[#e5dfd3]/60">
                <label className="flex items-start gap-2.5 font-body text-xs text-[#75664d] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={e => setAcceptTerms(e.target.checked)}
                    className="rounded mt-0.5 border-[#e5dfd3] focus:ring-[#a0d5ba]"
                    data-testid="checkout-terms-checkbox"
                  />
                  <span className="leading-relaxed">
                    Certifico que la dirección de entrega suministrada pertenece a la zona seleccionada, entiendo que no se hacen entregas fuera de cobertura. Acepto los términos y condiciones del sitio web <strong>www.keiksreposteria.com</strong>. Autorizo que mi información sea almacenada para uso exclusivo de Keiks Repostería.
                  </span>
                </label>
              </div>
              {publicConfig?.accept_online_payments !== false ? (
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full mt-5 bg-[#46371c] text-white hover:bg-[#5a4827] rounded-full font-body font-medium h-12 flex items-center justify-center gap-2 shadow-sm transition-all hover:scale-[1.02]"
                  data-testid="checkout-submit-btn"
                >
                  {submitting ? 'Procesando...' : paymentMethod === 'gateway' ? 'Proceder al Pago' : 'Confirmar Pedido'}
                </Button>
              ) : (
                <>
                  <div className="bg-[#fffbeb] border border-[#fef3c7] text-[#92400e] rounded-2xl p-4 text-xs font-body leading-relaxed space-y-2 mt-4 animate-fade-in">
                    <p className="font-bold text-[#b45309] text-sm">Finaliza tu pedido por WhatsApp</p>
                    <p>Por favor, realiza tu pedido directamente con nosotros a través de nuestro WhatsApp para darte una atención personalizada e indicarnos qué tortas tienes en tu carrito y procesar tu pedido manualmente.</p>
                  </div>
                  <a href={`https://wa.me/${whatsappNumber}?text=${generateWhatsAppMessage()}`} target="_blank" rel="noopener noreferrer" className="w-full mt-5 bg-[#25d366] text-white hover:bg-[#1ebea5] rounded-full font-body font-medium h-12 flex items-center justify-center gap-2 shadow-sm transition-all hover:scale-[1.02] animate-fade-in">
                    Pedir por WhatsApp
                  </a>
                </>
              )}
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}
